import isValid from 'date-fns/isValid';
import isEqual from 'lodash/isEqual';
import isNil from 'lodash/isNil';
import isString from 'lodash/isString';
import moment from 'moment';
import PropTypes from 'prop-types';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import ReactTooltip from 'react-tooltip';

import {
  formatLocalForAssetDashboardTile,
  formatUTCForAssetDashboardTile,
} from '../../Utils/dateFormatHelpers.util';
export default function TextWidget({
  data = [],
  displayTextAsGrid,
  showTimeSinceLastUpdated,
  updateRealTime,
}) {
  const timeSinceLastUpdatedEnabled = useMemo(
    () => showTimeSinceLastUpdated && updateRealTime,
    [showTimeSinceLastUpdated, updateRealTime]
  );
  const dataRef = useRef(data);
  const [state, updateState] = useState(data);

  const displayDates = state.map(({ timestamp, type }) => {
    return getTimeFormat(
      timestamp,
      timeSinceLastUpdatedEnabled,
      type === 'outputSignal'
    );
  });

  useInterval(() => {
    if (!timeSinceLastUpdatedEnabled) return;

    updateState([...state]);
  }, 1000);

  useEffect(() => {
    if (isEqual(data, dataRef?.current)) return;

    dataRef.current = data;
    updateState(data);
  }, [data]);

  const formatValue = useCallback((value) => {
    if (isNil(value)) return '--';

    return isString(value) && /\n/.exec(value) ? <pre>{value}</pre> : value; // add pre tags only for strings with newlines in them
  }, []);

  return (
    <div className="overflow-auto w-100 flex-grow-1">
      {displayTextAsGrid ? (
        <div className="h-100 container-fluid">
          <div className="h-100 row align-items-stretch justify-content-center">
            {state.map(({ name, unit, value }, i) => (
              <div
                className={`d-flex col-${
                  state?.length === 1
                    ? '12'
                    : state?.length === 2
                      ? 'auto'
                      : '6'
                } align-items-center justify-content-center ${
                  state?.length === 2 ? 'px-0' : ''
                }`}
                key={`${name}-${i}`}
              >
                <h1
                  className={`mb-0 display-${
                    state?.length <= 2 ? 1 : state?.length - 1
                  } text-muted font-weight-normal`}
                  data-tip
                  data-for={`${name}-${i}-tooltip`}
                >
                  {formatValue(value)}
                  <ReactTooltip id={`${name}-${i}-tooltip`} type="info">
                    <p className="mb-0 text-center">{name}</p>
                    <p className="mb-0 text-center">
                      <span>{formatValue(value)}</span>
                      <span>{!isNil(value) && unit ? ` ${unit}` : ''}</span>
                    </p>
                  </ReactTooltip>
                </h1>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <table
          className="text-left w-auto my-1 mx-3"
          style={{ tableLayout: 'fixed' }}
        >
          <thead>
            <tr>
              <th className="font-weight-light p-1 sticky-top bg-white text-text-light-gray">
                Signal Name
              </th>
              <th className="font-weight-light p-1 sticky-top bg-white text-text-light-gray">
                Value
              </th>
              <th className="font-weight-light p-1 sticky-top bg-white text-text-light-gray text-nowrap">
                {showTimeSinceLastUpdated && updateRealTime
                  ? 'Last Updated'
                  : 'Updated At'}
              </th>
            </tr>
          </thead>
          <tbody>
            {state.map(({ name, unit, value }, i) => (
              <tr key={`${name}-${i}`} className="text-break nine-tenth-text">
                <th className="text-muted font-weight-normal p-1">{name}</th>
                <th className="text-muted font-weight-normal p-1">
                  <span>{formatValue(value)}</span>
                  <span className="font-weight-light">
                    {!isNil(value) && unit ? ` ${unit}` : ''}
                  </span>
                </th>
                <th className="text-muted font-weight-normal p-1">
                  {displayDates[i]}
                </th>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

TextWidget.propTypes = {
  data: PropTypes.arrayOf(PropTypes.object),
  displayTextAsGrid: PropTypes.bool,
  showTimeSinceLastUpdated: PropTypes.bool,
  updateRealTime: PropTypes.bool,
};

TextWidget.defaultProps = { data: [] };

function useInterval(callback, delay) {
  const savedCallback = useRef();

  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  useEffect(() => {
    function tick() {
      savedCallback.current();
    }
    if (delay !== null) {
      let id = window.setInterval(tick, delay);
      return () => window.clearInterval(id);
    }
  }, [delay]);
}

function getTimeFormat(timestamp, showLastUpdated, isCalculatedSignal) {
  const date = new Date(timestamp);
  if (!isValid(date)) return '--';
  if (!showLastUpdated) {
    return isCalculatedSignal
      ? formatUTCForAssetDashboardTile(date)
      : formatLocalForAssetDashboardTile(date);
  }

  const diff = moment().diff(moment(timestamp));
  const duration = moment.duration(diff);

  const days = Math.floor(duration.asDays());
  const hours = String(Math.floor(duration.asHours() % 24)).padStart(2, '0');
  const mins = String(Math.floor(duration.asMinutes() % 60)).padStart(2, '0');
  const secs = String(Math.floor(duration.asSeconds() % 60)).padStart(2, '0');

  return `${days ? `${days}d ` : ''}${hours}:${mins}:${secs}`;
}
